export interface IProtectedUrl {
	url: string;
	token?: string;
}

export type FiltersType = Record<string, string | number | undefined>;

export async function PaginateFetchList<T>(
	input: string | IProtectedUrl | URL,
	filters?: FiltersType,
): Promise<Array<T>> {
	try {
		let newUrl =
			typeof input === "string"
				? input
				: (input as IProtectedUrl)?.url ?? (input as URL).toJSON();
		let token =
			typeof input === "string"
				? undefined
				: (input as IProtectedUrl)?.token ?? "";

		var completeUrl = Object.entries(filters ?? [])
			.filter((v) => v[1] !== "")
			.reduce((acc, v) => {
				return concatUrl(acc, v[0], v[1]);
			}, newUrl);

		let response: Response | null = await fetchWithTimeout({
			url: completeUrl,
			token: token,
		});

		if (response?.ok) {
			var res = await response?.json();
			var responseArray = res as Array<T>;
			if (responseArray.length > 0) return responseArray;
		} else {
			var err = await response?.text();
			console.error("PaginateRestLoader", err);
		}
	} catch (err) {
		console.error("PaginateRestLoader", err);
	}
	return [] as Array<T>;
}

export async function PaginatePostList<T>(
	input: string | URL | IProtectedUrl,
	filters?: FiltersType,
): Promise<Array<T>> {
	try {
		let newUrl =
			typeof input === "string"
				? input
				: (input as IProtectedUrl)?.url ?? (input as URL).toJSON();
		let token =
			typeof input === "string"
				? undefined
				: (input as IProtectedUrl)?.token ?? "";

		var completeUrl = Object.entries(filters ?? [])
			.filter((v) => v[1] !== "")
			.reduce((acc, v) => {
				return concatUrl(acc, v[0], v[1]);
			}, newUrl);

		let response: Response | null = await postWithTimeout({
			url: completeUrl,
			token: token,
		});

		if (response?.ok) {
			var res = await response?.json();
			var responseArray = res as Array<T>;
			if (responseArray.length > 0) return responseArray;
		} else {
			var err = await response?.text();
			console.error("PaginatePost", err);
		}
	} catch (err) {
		console.error("PaginatePost", err);
	}
	return [] as Array<T>;
}

export async function Fetch<T>(
	url: string | IProtectedUrl | URL,
): Promise<T | null> {
	let response: Response | null = await fetchWithTimeout(url);
	try {
		if (!response?.ok) return null;
		var result = await response?.text();
		return result as unknown as T;
	} catch (err) {
		console.error("Fetch", err);
	}
	return null;
}

export async function FetchList<T>(
	url: string | IProtectedUrl | URL,
): Promise<T[]> {
	let response: Response | null = await fetchWithTimeout(url);
	try {
		if (response?.ok) {
			var res = await response?.json();
			var responseArray = res as T[];
			if (responseArray.length > 0) return responseArray;
		} else {
			var err = await response?.text();
			console.error("FetchList", err);
		}
	} catch (err) {
		console.error("FetchList", err);
	}
	return [] as T[];
}

export async function FetchJson<T>(
	url: string | IProtectedUrl | URL,
): Promise<T | null> {
	let response: Response | null = await fetchWithTimeout(url);
	if (!response?.ok) return null;
	try {
		var result = await response?.json();
		return result as unknown as T;
	} catch (err) {
		console.error("FetchJson", err);
	}
	return null;
}

export async function FetchBlob<T>(
	url: string | IProtectedUrl | URL,
): Promise<T | null> {
	let response: Response | null = await fetchWithTimeout(url);
	try {
		if (response?.ok) {
			return (await response?.blob()) as unknown as T;
		} else {
			console.error(response?.text());
		}
	} catch (err) {
		console.error(err);
	}
	return null;
}

export async function Post<T>(
	url: string | IProtectedUrl | URL,
	body?: T,
): Promise<string> {
	let response: Response | null = await postWithTimeout<T>(url, body, 30000);
	try {
		var message = (await response?.text()) ?? "";
		return message;
	} catch (err) {
		console.error(err);
		return "error";
	}
}

export async function PostList<T, R>(
	url: string | IProtectedUrl | URL,
	body?: T,
) {
	const response: Response | null = await postWithTimeout<T>(url, body, 30000);
	try {
		if (response?.ok) {
			var res = await response?.json();
			var responseArray = res as R[];
			if (responseArray.length > 0) return responseArray;
		} else {
			var err = await response?.text();
		}
	} catch (err) {
		console.error(err);
	}
	return [] as R[];
}

export async function Put<T>(
	url: string | IProtectedUrl | URL,
	body?: T,
): Promise<string> {
	let response: Response | null = await postWithTimeout<T>(
		url,
		body,
		30000,
		"PUT",
	);
	try {
		var message = (await response?.text()) ?? "";
		return message;
	} catch (err) {
		console.error(err);
		return "error";
	}
}

export async function Delete<T>(
	url: string | IProtectedUrl | URL,
	body?: T,
): Promise<string> {
	let response: Response | null = await postWithTimeout<T>(
		url,
		body,
		30000,
		"DELETE",
	);
	try {
		var message = (await response?.text()) ?? "";
		return message;
	} catch (err) {
		console.error(err);
		return "error";
	}
}

async function postWithTimeout<T>(
	input: string | IProtectedUrl | URL,
	body?: T,
	timeout?: number,
	method?: string,
): Promise<Response | null> {
	const abortController = new AbortController();
	const id = setTimeout(() => abortController.abort(), timeout ?? 30000);
	let url =
		typeof input === "string"
			? input
			: (input as IProtectedUrl)?.url ?? (input as URL).toJSON();
	let token =
		typeof input === "string"
			? undefined
			: (input as IProtectedUrl)?.token ?? "";

	var request: RequestInit = {
		method: method ?? "POST",
		signal: abortController.signal,
		headers: token
			? {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
			  }
			: {
					"Content-Type": "application/json",
			  },
	};

	if (body !== undefined) {
		request.body = JSON.stringify(body);
	}

	var response: Response = {} as Response;
	try {
		response = await fetch(url, request);
		if (!response.ok) {
			var authError = response.headers.get("WWW-Authenticate");
			console.warn(authError, token);
		}
		clearTimeout(id);
	} catch (err) {
		console.error(err);
		return null;
	}
	return response;
}

async function fetchWithTimeout<T>(
	input: IProtectedUrl | string | URL,
	timeout?: number,
): Promise<Response | null> {
	const abortController = new AbortController();
	const id = setTimeout(() => abortController.abort(), timeout ?? 30000);

	let url =
		typeof input === "string"
			? input
			: (input as IProtectedUrl)?.url ?? (input as URL).toJSON();
	let token =
		typeof input === "string"
			? undefined
			: (input as IProtectedUrl)?.token ?? "";

	var request: RequestInit = {
		signal: abortController.signal,
		headers: token
			? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
			: undefined,
	};

	var response: Response = {} as Response;
	try {
		response = await fetch(url, request);
		if (!response.ok) {
			var authError = response.headers.get("WWW-Authenticate");
			console.warn(authError, token);
		}
		clearTimeout(id);
	} catch (err) {
		console.error(err);
		return null;
	}

	return response;
}

function concatUrl(
	newUrl: string,
	paramName: string,
	paramValue: string | number | undefined,
): string {
	return newUrl.indexOf("?") > 0
		? `${newUrl}&${paramName}=${paramValue}`
		: `${newUrl}?${paramName}=${paramValue}`;
}

export async function DownloadFile(
	url: string | IProtectedUrl | URL,
	filename: string,
): Promise<string> {
	try {
		var result = await FetchBlob<Blob>(url);
		if (result == null || result?.size === 0) return "";
		const objectURL = URL.createObjectURL(new Blob([result]));

		const link = document.createElement("a");
		link.href = objectURL;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	} catch (err) {
		console.error("DownloadFile", err);
		return "error";
	}
	return "";
}
